/**
 * CSV/Excel parsing utilities for attendee import/export
 */

/**
 * Parse CSV file content
 * @param {File} file - CSV file
 * @returns {Promise<Object>} - Parsed data with headers and rows
 */
export const parseCSV = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          reject(new Error('File is empty'));
          return;
        }
        
        // Parse CSV with proper handling of quoted fields
        const parseCSVLine = (line) => {
          const result = [];
          let current = '';
          let inQuotes = false;
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          
          result.push(current.trim());
          return result.map(field => field.replace(/^"|"$/g, '')); // Remove surrounding quotes
        };
        
        const headers = parseCSVLine(lines[0]);
        const rows = lines.slice(1).map(line => {
          const values = parseCSVLine(line);
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });
          return row;
        });
        
        resolve({
          headers,
          rows,
          totalRows: rows.length,
          fileName: file.name
        });
      } catch (error) {
        reject(new Error(`Failed to parse CSV: ${error.message}`));
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

/**
 * Parse Excel file using a simple XLSX parser
 * Note: For production, consider using a library like 'xlsx' or 'exceljs'
 * @param {File} file - Excel file
 * @returns {Promise<Object>} - Parsed data with headers and rows
 */
export const parseExcel = async (file) => {
  // For now, we'll suggest CSV format or implement a basic XLSX parser
  // In production, you'd want to use the 'xlsx' library
  throw new Error('Excel parsing requires additional library. Please use CSV format or install xlsx library.');
};

/**
 * Detect file type and parse accordingly
 * @param {File} file - File to parse
 * @returns {Promise<Object>} - Parsed data
 */
export const parseFile = async (file) => {
  const extension = file.name.split('.').pop().toLowerCase();
  
  switch (extension) {
    case 'csv':
      return parseCSV(file);
    case 'xlsx':
    case 'xls':
      return parseExcel(file);
    default:
      throw new Error(`Unsupported file format: ${extension}. Please use CSV or Excel files.`);
  }
};

/**
 * Generate CSV content from attendee data
 * @param {Array} attendees - Array of attendee objects
 * @param {Array} fields - Array of custom fields to include
 * @returns {string} - CSV content
 */
export const generateCSV = (attendees, fields) => {
  if (!attendees || attendees.length === 0) {
    return '';
  }
  
  // Create headers from field labels (use label if available, fallback to field_name)
  const headers = fields.map(field => {
    // Prioritize field_label for user-friendly headers
    return field.field_label || field.field_name || 'Unknown Field';
  });
  
  // Debug logging for advanced export
  console.log('🔍 Advanced CSV Export Debug:', {
    fieldsCount: fields.length,
    attendeesCount: attendees.length,
    headers: headers,
    fieldsStructure: fields.map(f => ({ name: f.field_name, label: f.field_label, type: f.field_type })),
    sampleAttendeeData: attendees[0]?.data
  });
  
  // Create CSV content
  const csvContent = [
    // Headers - properly escaped
    headers.map(header => `"${String(header).replace(/"/g, '""')}"`).join(','),
    
    // Data rows
    ...attendees.map(attendee => {
      return fields.map(field => {
        // Extract field value from attendee data
        let value = '';
        
        if (attendee.data && Array.isArray(attendee.data)) {
          // Try multiple ways to find the field data
          const fieldData = attendee.data.find(d => {
            const fieldName = d.field?.field_name || d.custom_field?.field_name;
            return fieldName === field.field_name;
          });
          
          if (fieldData) {
            // Try multiple property names for the value
            value = fieldData.value || fieldData.field_value || '';
          }
        }
        
        // Ensure value is a string and escape quotes properly
        const stringValue = String(value || '').replace(/"/g, '""');
        return `"${stringValue}"`;
      }).join(',');
    })
  ].join('\n');
  
  return csvContent;
};

/**
 * Download CSV content as file
 * @param {string} csvContent - CSV content
 * @param {string} filename - Filename for download
 */
export const downloadCSV = (csvContent, filename = 'attendees.csv') => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Validate CSV data structure
 * @param {Object} parsedData - Parsed CSV data
 * @returns {Object} - Validation result
 */
export const validateCSVData = (parsedData) => {
  const errors = [];
  const warnings = [];
  
  if (!parsedData.headers || parsedData.headers.length === 0) {
    errors.push('No headers found in CSV file');
  }
  
  if (!parsedData.rows || parsedData.rows.length === 0) {
    errors.push('No data rows found in CSV file');
  }
  
  // Check for empty headers
  const emptyHeaders = parsedData.headers.filter(h => !h || h.trim() === '');
  if (emptyHeaders.length > 0) {
    warnings.push(`${emptyHeaders.length} empty column headers found`);
  }
  
  // Check for duplicate headers
  const duplicateHeaders = parsedData.headers.filter((h, i) => 
    parsedData.headers.indexOf(h) !== i
  );
  if (duplicateHeaders.length > 0) {
    warnings.push(`Duplicate column headers found: ${duplicateHeaders.join(', ')}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Clean and normalize CSV data
 * @param {Object} parsedData - Parsed CSV data
 * @returns {Object} - Cleaned data
 */
export const cleanCSVData = (parsedData) => {
  // Clean headers - remove empty ones and normalize
  const cleanHeaders = parsedData.headers
    .map(h => h?.trim())
    .filter(h => h && h.length > 0);
  
  // Clean rows - remove completely empty rows
  const cleanRows = parsedData.rows
    .filter(row => {
      // Check if row has any non-empty values
      return Object.values(row).some(value => value && value.trim().length > 0);
    })
    .map(row => {
      // Clean individual cell values
      const cleanRow = {};
      cleanHeaders.forEach(header => {
        cleanRow[header] = row[header]?.trim() || '';
      });
      return cleanRow;
    });
  
  return {
    ...parsedData,
    headers: cleanHeaders,
    rows: cleanRows,
    totalRows: cleanRows.length
  };
};
